import { useEffect, useRef } from "react"
import cn from "classnames"
import Arrow from "public/assets/pagination-arrow.svg"

export default function Pagination({ pageCount, currentPage, setCurrentPage, scrollAnchor = ".page" }) {
  const isFirstMount = useRef(true)
  useEffect(() => {
    // prevent scrolling to scrollAnchor on page/component mount
    if (isFirstMount.current) {
      isFirstMount.current = false
      return
    }

    document.getElementById(scrollAnchor).scrollIntoView({ behavior: "smooth" })
  }, [currentPage, scrollAnchor])

  // no need for pagination, might as well call it!
  if (pageCount <= 1) return null

  currentPage = parseInt(currentPage)
  const DOTS = "..."

  function pagination() {
    const range = (startAt, endAt) => Array.from({ length: endAt - startAt + 1 }, (_, i) => i + startAt)

    // If dots are not required
    if (pageCount <= 5) return range(1, pageCount)

    // If only right dots should appear
    if (currentPage <= 3) {
      return [...range(1, 4), DOTS, pageCount]
    }

    // If only left dots should appear
    if (currentPage >= pageCount - 2) {
      return [1, DOTS, ...range(pageCount - 3, pageCount)]
    }

    // If both left and right dots should appear
    return [1, DOTS, ...range(currentPage - 1, currentPage + 1), DOTS, pageCount]
  }

  const PageNumbers = () =>
    pagination()?.map((pageNumber, idx) => {
      if (pageNumber === DOTS)
        return (
          <span key={"dots-" + idx} className="mx-2 -mb-3 flex cursor-default items-center justify-center text-l2">
            {DOTS}
          </span>
        )

      return (
        <button
          key={idx}
          disabled={pageNumber === +currentPage}
          onClick={() => setCurrentPage(pageNumber)}
          className={cn(
            `page-${pageNumber}`,
            "mx-2 flex justify-center text-l1 font-medium text-teal-dark hover:underline disabled:cursor-not-allowed",
            {
              underline: pageNumber === +currentPage,
            },
          )}
          aria-label={`Page ${pageNumber}${pageNumber === +currentPage ? ` (Current Page)` : ``}`}
        >
          {pageNumber}
        </button>
      )
    })

  return (
    <div className="mx-auto mb-12 mt-8 flex items-center justify-center px-10">
      <button
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
        className="prev-page mr-6 flex disabled:cursor-not-allowed"
        aria-label="Previous Page"
      >
        {/* adding custom class(es) overrides pagination-arrow class, so we have to add it back */}
        <Arrow className="pagination-arrow text-teal-dark" />
      </button>

      <PageNumbers />

      <button
        disabled={currentPage === pageCount}
        onClick={() => setCurrentPage(currentPage + 1)}
        className="next-page ml-6 flex hover:text-green disabled:cursor-not-allowed"
        aria-label="Next Page"
      >
        <Arrow className="pagination-arrow rotate-180 text-teal-dark" />
      </button>
    </div>
  )
}
